import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICONS = {
  offers: 'package',
  campaigns: 'zap',
  creatives: 'image',
  operators: 'users',
};

const CATEGORY_COLORS = {
  offers: 'text-emerald-400',
  campaigns: 'text-blue-400',
  creatives: 'text-purple-400',
  operators: 'text-amber-400',
};

const ResultItem = ({ type, item, onClick }) => {
  let label = '';
  let sublabel = '';

  if (type === 'offers') {
    label = item.name || item.offer_id;
    sublabel = `${item.vertical || ''} · ${item.status || ''} · Score: ${item.current_score || 0}`;
  } else if (type === 'campaigns') {
    label = item.name || item.campaign_id;
    sublabel = `${item.status || ''} · ${item.operator_name || ''}`;
  } else if (type === 'creatives') {
    label = item.name || item.creative_id;
    sublabel = `${item.channel || ''} · ${item.status || ''}`;
  } else if (type === 'operators') {
    label = item.name;
    sublabel = `${item.role || ''} · ${item.operator_code || ''}`;
  }

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-700/50 rounded-lg transition-colors text-left"
      data-testid={`search-result-${type}-${item.offer_id || item.campaign_id || item.creative_id || item.operator_id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{label}</p>
        <p className="text-xs text-slate-500 truncate">{sublabel}</p>
      </div>
    </button>
  );
};

export const SearchBar = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults(null); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`);
      setResults(res.data);
      setOpen(true);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 250);
  };

  const handleNavigate = (type, item) => {
    setOpen(false);
    setQuery('');
    if (type === 'offers') navigate(`/offers/${item.offer_id}`);
    else if (type === 'campaigns') navigate('/campaigns');
    else if (type === 'creatives') navigate(`/offers/${item.offer_id}`);
    else if (type === 'operators') navigate('/operators');
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasResults = results && results.total > 0;
  const categories = results?.results || {};

  return (
    <div ref={containerRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => { if (results) setOpen(true); }}
          placeholder="Search offers, campaigns, operators..."
          className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 transition-colors"
          data-testid="search-input"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-t border-b border-emerald-500"></div>
          </div>
        )}
      </div>

      {open && (
        <div className="absolute top-full mt-2 w-full bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto" data-testid="search-results-dropdown">
          {!hasResults ? (
            <div className="p-6 text-center">
              <p className="text-slate-400 text-sm">No results found for "{query}"</p>
            </div>
          ) : (
            <div className="p-2 space-y-3">
              {Object.entries(categories).map(([type, data]) => {
                if (!data.items || data.items.length === 0) return null;
                return (
                  <div key={type}>
                    <div className="flex items-center justify-between px-3 py-1">
                      <span className={`text-xs font-semibold uppercase ${CATEGORY_COLORS[type] || 'text-slate-400'}`}>
                        {type} ({data.total})
                      </span>
                    </div>
                    {data.items.map((item, i) => (
                      <ResultItem
                        key={i}
                        type={type}
                        item={item}
                        onClick={() => handleNavigate(type, item)}
                      />
                    ))}
                    {data.total > 5 && (
                      <button
                        onClick={() => { setOpen(false); navigate(`/${type}`); }}
                        className="w-full text-center text-xs text-emerald-400 hover:text-emerald-300 py-1"
                      >
                        View all {data.total} results
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
